import * as React from 'react'
import { autorun } from 'mobx'
import { inject, observer } from 'mobx-react'
import { FormattedMessage } from 'react-intl'
import { FaRuler } from 'react-icons/fa'

import footers from 'src/json/footerconfigs.json'
import productInfo from 'src/json/productinfo.json'
import Image from 'src/components/UI/Image/Image'
import DeliveryInfo from 'src/components/Product/DeliveryInfo/DeliveryInfo'
import ImageSection from 'src/components/Product/ImageSection/ImageSection'
import ShopSection from 'src/components/Product/ShopSection/ShopSection'
import Price from 'src/components/Product/Price/Price'
import ProductSEO from 'src/components/Product/ProductSEO/ProductSEO'
import SEO from 'src/components/SEO/SEO'
import Swatches from 'src/components/Product/Swatches/Swatches'
import TabList from 'src/components/Product/TabList/TabList'
import styles from 'src/components/Product/Product.module.scss'
import { fetchCentraData } from 'src/utils/api'
import { getCurrentSiteData } from 'src/helpers/getCurrentSiteData'
import {
  getPrice,
  isUnavailable,
  isItemAvailable,
  getProductComponentData,
  getLocalizedProductData,
  getRelatedProducts,
  getCategoryByProduct,
} from 'src/utils/product'
import PaymentMethods from 'src/components/Footer/PaymentMethods'
import ProductList from 'src/components/Modules/ProductList'
import FavSection from 'src/components/Product/FavSection/FavSection'
import BreadCrumbs from 'src/components/Breadcrumbs/Breadcrumbs'
import Brand from 'src/stores/brand'
import Ui from 'src/stores/ui'
import Preview from 'src/stores/preview'
import SizeChart from 'src/components/Product/SizeChart/SizeChart'

type Props = {
  cart: any
  pageContext: any
  preview: Preview
  ui: Ui
  brand: Brand
}

type State = {
  activeVariant: any
  activeVariantExtra: any
  fetched: boolean
  items: any
  unavailable: boolean | null
  showSizeChart: boolean
}

@inject('ui', 'cart', 'preview', 'brand')
@observer
export default class Product extends React.Component<Props, State> {
  disposer: any

  state = {
    activeVariant: {},
    activeVariantExtra: {},
    unavailable: null,
    items: null,
    fetched: false,
    showSizeChart: false,
  }

  constructor(props: Props) {
    super(props)
    const { brand } = props
    const { product, brandId } = props.pageContext
    brand.setBrandHeader(brandId, false)
    // Check stock here, set first buyable variant
    this.state.activeVariant = product && product.items ? product.items[0] : {}
    this.state.activeVariantExtra =
      product && product.items ? product.items[0] : {}
    this.state.items = product.items
  }

  componentDidMount() {
    // we dont know about availability until we have a country
    const { cart } = this.props
    const { product } = this.props.pageContext
    this.fetchProduct()
    this.disposer = autorun(() => {
      if (cart.country) {
        this.setState({
          unavailable: isUnavailable(product.markets, cart.country),
        })
      }
    })
  }

  fetchProduct = async () => {
    const { cart } = this.props
    const { product } = this.props.pageContext
    const response = await fetchCentraData(`products/${product.product}`)

    if (response && response.product) {
      const fetchedProduct = response.product
      const items = fetchedProduct.items
      const validItems = items.filter((item) =>
        isItemAvailable(cart.country, item.stock)
      )

      this.setState({
        items: items,
        activeVariant: validItems[0],
        activeVariantExtra: validItems[0],
        fetched: true,
      })
    }
  }

  setActiveVariant = (variant: any, extra?: boolean): void => {
    const key = variant.value
    const activeVariant = this.state.items.find((x: any) => x && x.item === key)

    if (extra) {
      this.setState({
        activeVariantExtra: activeVariant,
      })
    } else {
      this.setState({
        activeVariant: activeVariant,
      })
    }
  }

  render(): JSX.Element {
    const { brand, cart, pageContext, preview } = this.props
    const { locale } = pageContext
    let footer = getCurrentSiteData(footers)
    if (preview && preview.previewData) {
      const previewFooter = preview.getCurrentPageContext(
        footer.contentType,
        footer.locale
      )
      if (previewFooter) {
        footer = previewFooter
      }
    }

    let { product } = pageContext
    if (preview.previewProducts) {
      product = preview.getCurrentProductContext(product.product)
    }
    const { activeVariant, activeVariantExtra } = this.state
    const { name, description, infoColorSwatch } = getLocalizedProductData(
      product,
      locale
    )

    const { media } = product
    const { items, fetched } = this.state
    const ogImage = media[0] ? media[0].sources.standard.url : null

    const { tabInfo, bredcrumbsItems } = getProductComponentData(
      product,
      productInfo ? productInfo[0] : {},
      locale
    )
    const category = getCategoryByProduct(product, locale)
    const related = getRelatedProducts(product)
    const variationIds = product.relatedProducts
      ? product.relatedProducts.map((related: any) => related.product)
      : null
    const color = infoColorSwatch ? infoColorSwatch.display_name : ''
    const sizeType =
      product && product.infoSizeChart ? product.infoSizeChart : ''
    const seoName =
      brand && brand.activeBrand ? `${brand.activeBrand.brand} - ${name}` : name
    const brandId =
      brand && brand.activeBrand ? brand.activeBrand.centraId : null

    return product ? (
      <div className="brandTop">
        <SEO
          seoMetaDescription={description}
          seoOgimage={ogImage}
          type="product"
          seoTitle={seoName}
        />
        <ProductSEO product={product} />
        {this.state.showSizeChart && sizeType && (
          <SizeChart
            type={sizeType}
            handleClick={() => {
              this.setState({ showSizeChart: false })
            }}
          />
        )}
        <BreadCrumbs items={bredcrumbsItems} />
        <section className={styles.productWrapper}>
          <ImageSection media={media} />
          {/* <FavSection /> */}
          <div className={styles.productSection}>
            <h1>
              {name} {color}
            </h1>
            <div className={styles.price}>
              <Price noStyle price={getPrice(product.markets, cart.country)} />
            </div>
            <p>{product.sku}</p>
            {related && related.length > 1 && (
              <Swatches selected={product.product} products={related} />
            )}
            {sizeType && (
              <button
                className={styles.sizeChart}
                onClick={() => this.setState({ showSizeChart: true })}
              >
                <FaRuler size={'1.25em'} />
                <FormattedMessage
                  id="Product.sizeInfo"
                  defaultMessage="Storleksguide"
                />
              </button>
            )}
            <DeliveryInfo />
            <ShopSection
              items={items}
              fetched={fetched}
              cart={cart}
              activeVariant={activeVariant}
              setActiveVariant={this.setActiveVariant}
            />
            <PaymentMethods
              wrapperStyles={styles.paymentsTop}
              methods={footer.paymentMethods}
            />
            {tabInfo && <TabList items={tabInfo} />}
          </div>
        </section>
        {media && media.length > 2 && (
          <section className={styles.extraSection}>
            {media
              .slice(media.length - 2, media.length)
              .map((image: any, index: number) => (
                <div key={index} className={styles.extraImage}>
                  <Image isCritical src={image.sources.full.url} alt={name} />
                </div>
              ))}
            <h3 className={styles.extraTitle}>{name}</h3>
            <div className={styles.extraPrice}>
              <Price noStyle price={getPrice(product.markets, cart.country)} />
            </div>
            <ShopSection
              items={items}
              fetched={fetched}
              cart={cart}
              activeVariant={activeVariantExtra}
              setActiveVariant={this.setActiveVariant}
              extra={true}
            />
            <PaymentMethods
              wrapperStyles={styles.payments}
              methods={footer.paymentMethods}
            />
          </section>
        )}
        {variationIds && variationIds.length > 2 && (
          <React.Fragment>
            <div className={styles.divider} />
            <ProductList
              brand={brandId}
              fields={{
                title: (
                  <FormattedMessage
                    id="Product.ExtraSale.variations"
                    defaultMessage="Andra varianter"
                  />
                ),
                exclude: product.product,
                selection: {
                  fields: {
                    filterBy: "Product id's",
                    productIds: variationIds,
                  },
                },
              }}
            />
          </React.Fragment>
        )}
        <div className={styles.divider} />
        {category && (
          <ProductList
            brand={brandId}
            fields={{
              title: (
                <FormattedMessage
                  id="Product.ExtraSale.moreFrom"
                  defaultMessage="Mer från {category}"
                  values={{ category: category.toLowerCase() }}
                />
              ),
              exclude: product.product,
              selection: {
                fields: {
                  filterBy: 'Category id',
                  categoryId: product.canonicalCategory,
                },
              },
            }}
          />
        )}
      </div>
    ) : (
      <div />
    )
  }
}
